// Localização: frontend/src/components/Navigation/HeaderGestão.jsx
// Componente de Header para páginas de gestão com logo e menu de usuário

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { useSearch } from '../../context/SearchContext';
import { usePermissions } from '../../context/PermissionContext';
import { getMyProfile } from '../../api/api';
import { ChevronDown, User, Shield, LogOut, CreditCard, Search, X, Bell, AlertCircle, Key, Sparkles, ChartCandlestick } from 'lucide-react';
import { getNotificacoes } from '../../api/notificacaoApi';
import { usePlanAccess } from '../../hooks/usePlanAccess';
import { getUsuarioFotoUrl } from '../../utils/imageUtils';
import { AIProvider } from '../../context/AIContext';
import ChatInterfaceMini from '../AIAssistant/ChatInterfaceMini';
import SearchBar from '../Prontuario/SearchBar';

const HeaderGestão = () => {
    const { user, handleLogout, setUser } = useAuth();
    const { isHovered } = useSidebar();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { isModuleAvailable, currentPlanoId } = usePlanAccess();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const [userFotoUrl, setUserFotoUrl] = useState(null);
    const { searchTerm, setSearchTerm, searchPlaceholder, onSearchChange } = useSearch();
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [notificacoes, setNotificacoes] = useState([]);
    const [isNotificacoesOpen, setIsNotificacoesOpen] = useState(false);
    const [loadingNotificacoes, setLoadingNotificacoes] = useState(false);
    const notificacoesRef = useRef(null);

    // Estado para dropdown de IA (apenas Premium)
    const [isIADropdownOpen, setIsIADropdownOpen] = useState(false);
    // Key única para forçar criação de nova instância do ChatInterfaceMini
    // Só muda quando o dropdown é fechado e reaberto (não a cada clique)
    const [chatKey, setChatKey] = useState(0);
    const iaDropdownRef = useRef(null);
    const prevLocationRef = useRef(location.pathname);
    const wasOpenRef = useRef(false); // Rastrear se estava aberto anteriormente
    
    // Handler para abrir dropdown de IA - cria nova instância apenas se estava fechado
    const handleOpenIADropdown = () => {
        // Se já está aberto, não fazer nada (não apagar conversa atual)
        if (isIADropdownOpen) {
            return;
        }
        
        // Se estava fechado e agora vai abrir, criar nova instância
        console.log('[HeaderGestão] Abrindo nova conversa de IA, chatKey:', chatKey + 1);
        setChatKey(prev => prev + 1);
        wasOpenRef.current = true;
        setIsIADropdownOpen(true);
    };
    
    // Handler para fechar dropdown de IA
    const handleCloseIADropdown = () => {
        wasOpenRef.current = false;
        setIsIADropdownOpen(false);
    };


    // Verificar se usuário tem plano Premium (ID: 3) e acesso ao módulo financeiro
    const isPremium = currentPlanoId === 3 && isModuleAvailable('financeiro');

    // Verificar se usuário tem acesso ao módulo financeiro
    const { checkPermission } = usePermissions();
    const hasFinanceiroAccess = checkPermission('financeiro', 'can_access') && isModuleAvailable('financeiro');

    // Verificar se usuário tem acesso ao módulo de IA (permissão RBAC + disponibilidade no plano)
    const hasIAAccess = checkPermission('ia', 'can_access') && isModuleAvailable('ia');

    // Buscar fotoURL do perfil se não estiver no contexto
    useEffect(() => {
        // Se já tem fotoURL no user e não é string vazia, usar ela
        if (user?.fotoURL && user.fotoURL.trim() !== '') {
            setUserFotoUrl(user.fotoURL);
            return;
        }

        // Se não tem fotoURL válida e ainda não buscou, buscar do perfil
        const fetchUserPhoto = async () => {
            try {
                const response = await getMyProfile();
                const profileData = response.data;
                if (profileData?.fotoURL && profileData.fotoURL.trim() !== '') {
                    setUserFotoUrl(profileData.fotoURL);
                    // Atualizar contexto também
                    if (setUser) {
                        setUser(prevUser => ({
                            ...prevUser,
                            fotoURL: profileData.fotoURL
                        }));
                    }
                }
            } catch (err) {
                console.error('[HeaderGestão] Erro ao buscar foto do perfil:', err);
            }
        };

        if (user && (!user.fotoURL || user.fotoURL.trim() === '')) {
            fetchUserPhoto();
        }
    }, [user?.id, setUser]); // Dependência apenas do ID do usuário para evitar múltiplas chamadas

    // Atualizar fotoUrl local quando o contexto for atualizado (ex: após upload de foto)
    useEffect(() => {
        if (user?.fotoURL && user.fotoURL.trim() !== '') {
            setUserFotoUrl(user.fotoURL);
        } else if (user?.fotoURL === '' || user?.fotoURL === null) {
            // Limpar fotoUrl se foi removida
            setUserFotoUrl(null);
        }
    }, [user?.fotoURL]);

    // Buscar notificações (agendamentos, assinaturas e outras)
    useEffect(() => {
        const fetchNotificacoes = async () => {
            try {
                setLoadingNotificacoes(true);
                const response = await getNotificacoes();
                if (response.success) {
                    setNotificacoes(response.notificacoes || []);
                }
            } catch (error) {
                console.error('[HeaderGestão] Erro ao buscar notificações:', error);
            } finally {
                setLoadingNotificacoes(false);
            }
        };

        if (user) {
            fetchNotificacoes();
            // Atualizar notificações a cada 5 minutos
            const interval = setInterval(fetchNotificacoes, 5 * 60 * 1000);
            return () => clearInterval(interval);
        }
    }, [user]);

    // Fechar menu ao clicar fora (EXCETO dropdown de IA - ele só fecha pelo botão)
    useEffect(() => {
        const handleClickOutside = (event) => {
            // NÃO fechar dropdown de IA automaticamente - ele só fecha quando o usuário clicar no botão
            // O dropdown de IA permanece aberto para permitir conversas contínuas
            
            // Outros dropdowns fecham normalmente
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            if (notificacoesRef.current && !notificacoesRef.current.contains(event.target)) {
                setIsNotificacoesOpen(false);
            }
        };

        if (isMenuOpen || isNotificacoesOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen, isNotificacoesOpen]);

    // Função para expandir para página completa
    const handleExpandIA = () => {
        setIsIADropdownOpen(false);
        navigate('/assistente-ia');
    };

    // Obter URL da foto do usuário (prioriza userFotoUrl do estado local, depois do contexto)
    // Verifica se existe e não é string vazia
    const fotoUrlRaw = userFotoUrl || user?.fotoURL;
    const fotoUrl = fotoUrlRaw && fotoUrlRaw.trim() !== '' ? getUsuarioFotoUrl(fotoUrlRaw) : null;

    // Iniciais do nome para avatar padrão
    const getIniciais = () => {
        if (user?.nome || user?.nome_completo) {
            const nome = user.nome || user.nome_completo;
            const partes = nome.trim().split(' ');
            if (partes.length >= 2) {
                return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
            }
            return nome[0].toUpperCase();
        }
        return 'U';
    };

    // Obter apenas o primeiro nome
    const getPrimeiroNome = () => {
        if (user?.nome || user?.nome_completo) {
            const nome = user.nome || user.nome_completo;
            const partes = nome.trim().split(' ');
            return partes[0];
        }
        return 'Usuário';
    };

    const handleMenuClick = (action) => {
        setIsMenuOpen(false);

        switch (action) {
            case 'perfil':
                navigate('/profile');
                break;
            case 'permissoes':
                navigate('/config/permissoes');
                break;
            case 'privacidade':
                navigate('/privacidade');
                break;
            case 'assinatura':
                navigate('/config/assinatura/gerenciar');
                break;
            case 'sair':
                handleLogout();
                navigate('/login');
                break;
            default:
                break;
        }
    };

    // Determinar se deve mostrar o campo de busca baseado na rota
    const shouldShowSearch = () => {
        const searchRoutes = [
            '/pacientes',           // Lista de Pacientes
            '/agendamentos',       // Agendamentos
            '/tarefas',            // Tarefas Agendadas
            '/usuarios',           // Gestão de Usuários
            '/medicamentos',      // Medicamentos
            '/equipamentos',       // Equipamentos
        ];
        return searchRoutes.some(route => location.pathname.startsWith(route));
    };

    // Verificar se está na página de prontuário
    const isProntuarioPage = location.pathname.includes('/prontuario');
    const pacienteId = isProntuarioPage && params.pacienteid ? parseInt(params.pacienteid, 10) : null;

    // 🎯 IMPORTANTE: Todos os hooks devem ser chamados ANTES de qualquer early return
    const handleSearchChange = useCallback((e) => {
        if (!e || !e.target) return;

        const value = e.target.value;

        // Atualizar o estado de busca diretamente
        setSearchTerm(value);

        // Callback opcional para páginas que precisam de lógica adicional
        if (onSearchChange && typeof onSearchChange === 'function') {
            try {
                onSearchChange(value);
            } catch (error) {
                console.error('[HeaderGestão] Erro ao chamar onSearchChange:', error);
            }
        }
    }, [setSearchTerm, onSearchChange]);

    const handleClearSearch = () => {
        setSearchTerm('');
        if (onSearchChange) {
            onSearchChange('');
        }
    };

    // Ajustar posição do header baseado no estado de hover do Sidebar
    const headerLeft = isHovered ? 'lg:left-[250px]' : 'lg:left-[80px]';
    const showSearch = shouldShowSearch();
    const showProntuarioSearch = isProntuarioPage && pacienteId;

    // Verificar se há usuário (DEPOIS de todos os hooks)
    if (!user) {
        return null;
    }

    return (
        <header className={`bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-40 ${headerLeft} transition-all duration-300 ${showProntuarioSearch ? 'min-h-[64px]' : 'h-16'}`}>
            <div className="px-4 sm:px-6 lg:px-8">
                <div className={`flex items-center ${showProntuarioSearch ? 'py-2' : 'h-16'} gap-4 ${(showSearch || showProntuarioSearch) ? 'justify-between' : 'justify-end'}`}>
                    {/* Campo de Busca do Prontuário - Lado Esquerdo (apenas na página de prontuário) */}
                    {showProntuarioSearch && (
                        <div className="flex-1 max-w-md">
                            <SearchBar 
                                pacienteId={pacienteId}
                                placeholder="Buscar no prontuário (medicações, registros, tarefas, documentos...)"
                                compact={true}
                            />
                        </div>
                    )}

                    {/* Campo de Busca Padrão - Lado Esquerdo (apenas em rotas específicas) */}
                    {showSearch && !showProntuarioSearch && (
                        <div className="flex-1 max-w-md">
                            <div className={`relative transition-all duration-300 ${isSearchFocused ? 'scale-[1.02]' : ''}`}>
                                <Search
                                    className={`absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-colors duration-200 ${isSearchFocused ? 'text-emerald-600' : 'text-gray-400'
                                        }`}
                                />
                                <input
                                    type="text"
                                    value={searchTerm || ''}
                                    onChange={handleSearchChange}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setIsSearchFocused(false)}
                                    placeholder={searchPlaceholder || 'Buscar...'}
                                    className="
                                        w-full py-1.5 sm:py-2 pl-8 sm:pl-10 pr-8 sm:pr-10
                                        bg-gray-50 border border-gray-300 rounded-lg
                                        focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white
                                        transition-all duration-200 ease-in-out
                                        text-xs sm:text-sm text-gray-700 placeholder-gray-400
                                        hover:border-gray-400 hover:bg-white
                                    "
                                    aria-label="Buscar"
                                    autoComplete="off"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="absolute right-1.5 sm:right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 transition-colors duration-150"
                                        aria-label="Limpar busca"
                                    >
                                        <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notificações e Menu do Usuário - Lado Direito */}
                    <div className="flex items-center gap-3 ml-auto">
                        {/* Botão de Financeiro - direto para Dashboard */}
                        {hasFinanceiroAccess && (
                            <button
                                onClick={() => navigate('/financeiro/dashboard')}
                                className={`relative p-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none ${location.pathname.startsWith('/financeiro')
                                    ? 'bg-emerald-50 hover:bg-emerald-100'
                                    : ''
                                    }`}
                                aria-label="Dashboard Financeiro"
                                title="Dashboard Financeiro"
                            >
                                <ChartCandlestick className={`h-5 w-5 ${location.pathname.startsWith('/financeiro')
                                    ? 'text-emerald-600'
                                    : 'text-gray-600'
                                    }`} />
                            </button>
                        )}

                        {/* Botão de IA (apenas com permissão) */}
                        {hasIAAccess && (
                            <div className="relative" ref={iaDropdownRef}>
                                <button
                                    onClick={handleOpenIADropdown}
                                    className="relative p-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none bg-purple-50"
                                    aria-label="Assistente de IA"
                                    title="Assistente de IA"
                                >
                                    <Sparkles className="h-5 w-5 text-purple-600" />
                                </button>

                                {/* Dropdown de IA com ChatInterface Mini - cria nova instância apenas ao reabrir */}
                                {isIADropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-[90vw] sm:w-[420px] max-w-[95vw] sm:max-w-none z-[60]">
                                        <AIProvider key={chatKey}>
                                            <ChatInterfaceMini 
                                                key={chatKey}
                                                onExpand={handleExpandIA} 
                                                onClose={handleCloseIADropdown}
                                            />
                                        </AIProvider>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Ícone de Notificações */}
                        <div className="relative" ref={notificacoesRef}>
                            <button
                                onClick={() => setIsNotificacoesOpen(!isNotificacoesOpen)}
                                className="relative p-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none"
                                aria-label="Notificações"
                            >
                                <Bell className={`h-5 w-5 ${notificacoes.length > 0 ? 'text-higeia-dark' : 'text-gray-400'}`} />
                                {notificacoes.length > 0 && (
                                    <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                                )}
                            </button>

                            {/* Dropdown de Notificações */}
                            {isNotificacoesOpen && (
                                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[60] max-h-96 overflow-y-auto">
                                    <div className="p-4 border-b border-gray-200">
                                        <h3 className="text-sm font-semibold text-gray-900">Notificações</h3>
                                    </div>
                                    {loadingNotificacoes ? (
                                        <div className="p-4 text-center text-sm text-gray-500">
                                            Carregando...
                                        </div>
                                    ) : notificacoes.length === 0 ? (
                                        <div className="p-4 text-center text-sm text-gray-500">
                                            Nenhuma notificação
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-200">
                                            {notificacoes.map((notificacao) => (
                                                <div
                                                    key={notificacao.id}
                                                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${notificacao.urgente ? 'bg-red-50' : ''
                                                        }`}
                                                    onClick={() => {
                                                        setIsNotificacoesOpen(false);
                                                        if (notificacao.url) {
                                                            navigate(notificacao.url);
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className={`flex-shrink-0 mt-0.5 ${notificacao.urgente ? 'text-red-600' : 'text-yellow-600'
                                                            }`}>
                                                            <AlertCircle className="h-5 w-5" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900">
                                                                {notificacao.titulo}
                                                            </p>
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                {notificacao.mensagem}
                                                            </p>
                                                            {notificacao.acao && notificacao.url && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setIsNotificacoesOpen(false);
                                                                        navigate(notificacao.url);
                                                                    }}
                                                                    className="text-xs text-higeia-dark hover:text-higeia-medium mt-2 font-medium"
                                                                >
                                                                    {notificacao.acao} →
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Menu do Usuário */}
                        <div className="relative flex-shrink-0" ref={menuRef}>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 hover:shadow-sm transition-all duration-300 ease-in-out focus:outline-none focus-visible:outline-none active:outline-none active:scale-[0.98]"
                                aria-label="Menu do usuário"
                                aria-expanded={isMenuOpen}
                            >
                                {/* Foto do Usuário ou Avatar com Iniciais */}
                                <div className="relative">
                                    {fotoUrl ? (
                                        <img
                                            src={fotoUrl}
                                            alt={`${getPrimeiroNome()}`}
                                            className="h-10 w-10 rounded-full object-cover border-2 border-gray-200 transition-all duration-300 hover:border-gray-300"
                                            onError={(e) => {
                                                // Fallback para avatar com iniciais se a imagem falhar
                                                e.target.style.display = 'none';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback) {
                                                    fallback.style.display = 'flex';
                                                }
                                            }}
                                            onLoad={(e) => {
                                                // Garantir que a imagem está visível quando carregar com sucesso
                                                e.target.style.display = 'block';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback) {
                                                    fallback.style.display = 'none';
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className={`h-10 w-10 rounded-full bg-higeia-dark flex items-center justify-center text-white font-semibold text-sm transition-all duration-300 hover:bg-higeia-dark/90 ${fotoUrl ? 'hidden' : 'flex'
                                            }`}
                                    >
                                        {getIniciais()}
                                    </div>
                                </div>

                                {/* Primeiro Nome do Usuário (oculto em mobile) */}
                                <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[150px] truncate">
                                    {getPrimeiroNome()}
                                </span>

                                {/* Ícone de Chevron */}
                                <ChevronDown
                                    className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isMenuOpen ? 'transform rotate-180' : ''
                                        }`}
                                />
                            </button>

                            {/* Menu Suspenso */}
                            {isMenuOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[60]">
                                    {/* Meu Perfil */}
                                    <button
                                        onClick={() => handleMenuClick('perfil')}
                                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <User className="h-4 w-4 text-gray-500" />
                                        <span>Meu Perfil</span>
                                    </button>

                                    {/* Permissões */}
                                    <button
                                        onClick={() => handleMenuClick('permissoes')}
                                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <Key className="h-4 w-4 text-gray-500" />
                                        <span>Permissões</span>
                                    </button>

                                    {/* Privacidade */}
                                    <button
                                        onClick={() => handleMenuClick('privacidade')}
                                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <Shield className="h-4 w-4 text-gray-500" />
                                        <span>Privacidade</span>
                                    </button>

                                    {/* Assinatura */}
                                    <button
                                        onClick={() => handleMenuClick('assinatura')}
                                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                    >
                                        <CreditCard className="h-4 w-4 text-gray-500" />
                                        <span>Assinatura</span>
                                    </button>

                                    {/* Divisor */}
                                    <div className="border-t border-gray-200 my-1"></div>

                                    {/* Sair */}
                                    <button
                                        onClick={() => handleMenuClick('sair')}
                                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        <span>Sair</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default HeaderGestão;

